import { useI18n } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { Cog, Users, Server, RefreshCw, Mail, Calendar, Bell, List as ListIcon, Bot, Key, Building2, MessageSquarePlus, ToggleRight } from 'lucide-react';
import apiService from '../services/apiService';
import CustomerAdmin from './CustomerAdmin';
import AiTab, { useAiTab } from './admin/AiTab';
import ApiKeysTab, { useApiKeysTab } from './admin/ApiKeysTab';
import FeaturesTab, { useFeaturesTab } from './admin/FeaturesTab';
import FeedbackTab, { useFeedbackTab } from './admin/FeedbackTab';
import LogsTab, { useLogsTab } from './admin/LogsTab';
import MailboxTab, { useMailboxTab } from './admin/MailboxTab';
import MarkersTab, { useMarkersTab } from './admin/MarkersTab';
import NotificationsTab, { useNotificationsTab } from './admin/NotificationsTab';
import ServerTab, { useServerTab } from './admin/ServerTab';
import UsersTab from './admin/UsersTab';
import './AdminPanel.css';

const AdminPanel = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // #312: Postfach-Ueberwachung (admin/MailboxTab.js) und Kalender-Marker
  // (admin/MarkersTab.js). loadData() fuellt beide; "Jetzt verarbeiten" laedt
  // danach neu, deshalb reicht das Panel loadData als Pfeil herein (beim
  // Aufruf des Hooks ist die Funktion weiter unten noch nicht deklariert).
  const mailboxTab = useMailboxTab({ setError, setSaving, loadData: () => loadData() });
  const markersTab = useMarkersTab({ setError, setSaving });
  // #312: Hinweisleiste + Release-Note (admin/NotificationsTab.js) und KI
  // (admin/AiTab.js). Die verzoegerten Speicher-Timer und der Backfill-Poll
  // haengen damit weiter an der Lebensdauer des Panels (#307).
  const notificationsTab = useNotificationsTab({ setError });
  const aiTab = useAiTab({ setError, setSaving });
  

  

  // #312: System-Log-Reiter in admin/LogsTab.js; Zustand lebt so lange wie das Panel
  const logsTab = useLogsTab();

  // #312: Servereinstellungen in admin/ServerTab.js (Allowlist)
  const serverTab = useServerTab({ setError, setSaving });

  // #520: alle Feature-Schalter (admin/FeaturesTab.js). Der Stand steuert auch
  // die Reiterleiste („Kunden“) und das Ausgrauen im KI-Reiter, deshalb liest
  // das Panel ihn hier ab.
  const featuresTab = useFeaturesTab({ setError });

  // #312: API-Keys-Reiter in admin/ApiKeysTab.js; der einmal angezeigte Klartext-Key
  // uebersteht so wie bisher den Reiterwechsel
  const apiKeysTab = useApiKeysTab({ setError });

  // #312: Feedback-Reiter in admin/FeedbackTab.js; Fehler laufen weiter ueber die Panel-Meldung
  const feedbackTab = useFeedbackTab({ setError });

  // Kundenstamm (#35/#87): komplett in components/CustomerAdmin.js ausgelagert

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [usersResponse, allowedEmailsResponse, mailboxResponse, markersResponse, notificationResponse, aiResponse, releaseNoteResponse, releaseVersionsResponse, featuresResponse] = await Promise.all([
        apiService.getUsers(),
        // #627: eigene Admin-Route; scheitert sie, sperrt der Reiter das Speichern
        apiService.getAllowedEmails().catch(() => null),
        apiService.getMailboxConfigs().catch(() => ({ configs: [] })), // Fallback für neue Installation
        apiService.getCalendarMarkersAdmin().catch(() => ({ markers: [] })), // Fallback für neue Installation
        apiService.getNotificationConfigAdmin().catch(() => ({ config: { isActive: false, message: '' } })),
        apiService.getAiIntegrationConfig().catch(() => ({ config: { baseUrl: 'https://api.openai.com/v1', selectedModel: '', searchPrompt: "Use only the information provided in the context to answer the user's question. Keep the answer concise. Reply in the language of the context.", summaryPrompt: 'You are a helpful assistant. Given the ticket history context (emails, comments, Teams messages; no images), produce a concise, structured summary highlighting key points, decisions, open questions, and next steps. Reply in the language of the context.', perQueryFragmentCap: 60, globalFragmentCap: 300, contextCharCap: 12000, fullTicketsCount: 8 } })),
        apiService.getReleaseNoteAdmin().catch(() => ({ note: { title: '', content: '', isActive: false } })),
        // #195: importierte Versions-Historie nur zur Anzeige (read-only)
        apiService.getReleaseNoteVersions().catch(() => ({ versions: [] })),
        // #520: scheitert das, zeigt der Reiter „Features“ einen Hinweis und nichts wird ausgegraut
        apiService.getAdminFeatures().catch(() => null)
      ]);
      
      setUsers(usersResponse.users || []);
      serverTab.uebernehmeGeladen(allowedEmailsResponse);
      const cfgs = mailboxResponse.configs || [];
      mailboxTab.setMailboxConfigs(cfgs);
      // Drafts initialisieren
      const initialDrafts = {};
      (cfgs || []).forEach(c => {
        if (c && c.id != null) {
          initialDrafts[c.id] = {
            autoReplyNewTicketTemplate: c.autoReplyNewTicketTemplate || '',
            autoReplyExistingTicketTemplate: c.autoReplyExistingTicketTemplate || ''
          };
        }
      });
      mailboxTab.setTemplateDrafts(initialDrafts);
      markersTab.setCalendarMarkers(markersResponse.markers || []);
      notificationsTab.setNotificationConfig(notificationResponse.config || { isActive: false, message: '' });
      notificationsTab.setReleaseNote(releaseNoteResponse.note || { title: '', content: '', isActive: false });
      notificationsTab.setReleaseNoteVersions(releaseVersionsResponse.versions || []);
      featuresTab.setStand(featuresResponse);
      const cfg = aiResponse.config || { baseUrl: 'https://api.openai.com/v1', selectedModel: '', searchPrompt: "Use only the information provided in the context to answer the user's question. Keep the answer concise. Reply in the language of the context.", summaryPrompt: 'You are a helpful assistant. Given the ticket history context (emails, comments, Teams messages; no images), produce a concise, structured summary highlighting key points, decisions, open questions, and next steps. Reply in the language of the context.', debugEnabled: false, perQueryFragmentCap: 60, globalFragmentCap: 300, contextCharCap: 12000, fullTicketsCount: 8 };
      aiTab.setAiConfig(cfg);
      // Modelle beim Laden automatisch holen, wenn Key vorhanden ist
      try {
        const resp = await apiService.testAiIntegration({ baseUrl: cfg.baseUrl, chatOnly: true });
        let models = resp.models || [];
        if (cfg.selectedModel && !models.some(m => m.id === cfg.selectedModel)) {
          models = [{ id: cfg.selectedModel, name: cfg.selectedModel }, ...models];
        }
        aiTab.setAiModels(models);
      } catch (_) {
        // still ok if it fails; user can press Test manuell
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 403) {
        setError(t('admin.error.permission', 'Keine Berechtigung für den Admin-Bereich.'));
      } else {
        setError(t('admin.error.load', 'Fehler beim Laden der Admin-Daten.'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="admin-panel">
          <div className="loading">
            <RefreshCw className="animate-spin" size={32} />
            <p>{t('admin.loading', 'Lade Admin-Daten...')}</p>
          </div>
        </div>
      </div>
    );
  }

  // #87: Kundenverwaltung als eigene Komponente (auch für Manager unter /customers)
  const renderCustomers = () => <CustomerAdmin />;

  return (
    <div className="container">
      <div className="admin-panel">
        <div className="admin-header">
          <h1>
            <Cog size={32} style={{ color: 'var(--color-primary)' }} />
            {t('admin.title', 'Administration')}
          </h1>
          <button 
            onClick={loadData}
            className="btn btn-secondary"
            disabled={loading}
          >
            <RefreshCw size={16} />
            {t('admin.refresh', 'Aktualisieren')}
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}


        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={16} />
            {t('admin.tabs.users', 'Benutzerverwaltung')}
          </button>
          <button
            className={`tab-button ${activeTab === 'server' ? 'active' : ''}`}
            onClick={() => setActiveTab('server')}
          >
            <Server size={16} />
            {t('admin.tabs.server', 'Servereinstellungen')}
          </button>
          <button
            className={`tab-button ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            <ToggleRight size={16} />
            {t('admin.tabs.features', 'Features')}
          </button>
          <button
            className={`tab-button ${activeTab === 'mailbox' ? 'active' : ''}`}
            onClick={() => setActiveTab('mailbox')}
          >
            <Mail size={16} />
            {t('admin.tabs.mailbox', 'Postfach-Überwachung')}
          </button>
          <button
            className={`tab-button ${activeTab === 'markers' ? 'active' : ''}`}
            onClick={() => setActiveTab('markers')}
          >
            <Calendar size={16} />
            {t('admin.tabs.markers', 'Kalender Marker')}
          </button>
          <button
            className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Bell size={16} />
            {t('admin.tabs.notifications', 'Benachrichtigungen')}
          </button>
          <button
            className={`tab-button ${activeTab === 'apiKeys' ? 'active' : ''}`}
            onClick={() => { setActiveTab('apiKeys'); apiKeysTab.loadApiKeys(); }}
          >
            <Key size={16} />
            {t('admin.tabs.apiKeys', 'API-Keys')}
          </button>
          {featuresTab.features?.customers !== false && (
            <button
              className={`tab-button ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              <Building2 size={16} />
              {t('admin.tabs.customers', 'Kunden')}
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => { setActiveTab('feedback'); feedbackTab.loadFeedback(); }}
          >
            <MessageSquarePlus size={16} />
            {t('admin.tabs.feedback', 'Feedback')}
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => { setActiveTab('logs'); logsTab.loadLogs(); }}
          >
            <ListIcon size={16} />
            Log
          </button>
          <button
            className={`tab-button ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            <Bot size={16} />
            AI Integration
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'users' && <UsersTab users={users} setUsers={setUsers} loading={loading} saving={saving} setSaving={setSaving} setError={setError} />}
          {activeTab === 'server' && <ServerTab tab={serverTab} saving={saving} featuresTab={featuresTab} />}
          {activeTab === 'features' && <FeaturesTab tab={featuresTab} />}
          {activeTab === 'mailbox' && <MailboxTab tab={mailboxTab} saving={saving} loading={loading} />}
          {activeTab === 'markers' && <MarkersTab tab={markersTab} saving={saving} />}
          {activeTab === 'notifications' && <NotificationsTab tab={notificationsTab} />}
          {activeTab === 'apiKeys' && <ApiKeysTab tab={apiKeysTab} users={users} />}
          {activeTab === 'customers' && renderCustomers()}
          {activeTab === 'feedback' && <FeedbackTab tab={feedbackTab} />}
          {activeTab === 'logs' && <LogsTab tab={logsTab} />}
          {activeTab === 'ai' && <AiTab tab={aiTab} saving={saving} features={featuresTab.features} />}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 